export const OAUTH_LOGIN_AUDIT_KEY = 'tb:oauth-login-audit';
/** Set while Google/SSO redirect is in flight so a prior password session is not restored. */
export const OAUTH_PENDING_KEY = 'tb:oauth:pending';
export const OAUTH_CALLBACK_ERROR_KEY = 'tb:oauth:callback-error';

export type OAuthLoginAuditPayload = {
  provider: string;
  organizationId?: string;
};

export function stashOAuthLoginAudit(payload: OAuthLoginAuditPayload): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(OAUTH_LOGIN_AUDIT_KEY, JSON.stringify(payload));
  } catch {
    /* ignore quota / private mode */
  }
}

export function consumeOAuthLoginAudit(): OAuthLoginAuditPayload | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(OAUTH_LOGIN_AUDIT_KEY);
    sessionStorage.removeItem(OAUTH_LOGIN_AUDIT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as OAuthLoginAuditPayload;
    if (parsed && typeof parsed.provider === 'string' && parsed.provider.length > 0) {
      return parsed;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function markOAuthPending(provider = 'google'): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(OAUTH_PENDING_KEY, provider);
    sessionStorage.removeItem(OAUTH_CALLBACK_ERROR_KEY);
  } catch {
    /* ignore */
  }
}

export function clearOAuthPending(): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(OAUTH_PENDING_KEY);
  } catch {
    /* ignore */
  }
}

export function isOAuthPending(): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  try {
    return Boolean(sessionStorage.getItem(OAUTH_PENDING_KEY));
  } catch {
    return false;
  }
}

export function markOAuthCallbackError(message: string): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(OAUTH_CALLBACK_ERROR_KEY, message);
  } catch {
    /* ignore */
  }
}

export function consumeOAuthCallbackError(): string | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(OAUTH_CALLBACK_ERROR_KEY);
    sessionStorage.removeItem(OAUTH_CALLBACK_ERROR_KEY);
    return raw?.trim() || null;
  } catch {
    return null;
  }
}

/**
 * Read Supabase/Google OAuth failure params from the return URL
 * (e.g. ?error=server_error&error_description=Unable+to+exchange+external+code).
 */
export function consumeOAuthErrorFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const fromSearch = new URLSearchParams(window.location.search);
    const fromHash = new URLSearchParams(
      window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash,
    );
    const error = (fromSearch.get('error') || fromHash.get('error') || '').trim();
    const description = decodeURIComponent(
      (fromSearch.get('error_description') || fromHash.get('error_description') || '').replace(/\+/g, ' '),
    ).trim();
    const code = (fromSearch.get('error_code') || fromHash.get('error_code') || '').trim();
    if (!error && !description) return null;

    // Strip error params so refresh does not re-show the same banner forever.
    fromSearch.delete('error');
    fromSearch.delete('error_description');
    fromSearch.delete('error_code');
    fromSearch.delete('sb');
    const query = fromSearch.toString();
    const next = `${window.location.pathname}${query ? `?${query}` : ''}`;
    window.history.replaceState({}, document.title, next);

    const lower = `${error} ${description} ${code}`.toLowerCase();
    if (lower.includes('exchange external code') || lower.includes('unexpected_failure')) {
      return (
        'Google sign-in failed because Supabase could not exchange the Google code. ' +
        'In Supabase → Authentication → Providers → Google, set Client ID and Client Secret ' +
        'to the matching Tools & Bubbles Google Cloud Web client (do not mix the ServiceApp secret). ' +
        'Keep both Client IDs comma-separated if ServiceApp Google is also configured.'
      );
    }
    return description || `Google sign-in failed (${error || code || 'unknown error'}).`;
  } catch {
    return null;
  }
}
